import { getRecipeShareText } from '../utils/shareUtils';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import ExplorerHeader from '../components/explorer/ExplorerHeader';
import RecipeGrid from '../components/explorer/RecipeGrid';
import PaginationControls from '../components/explorer/PaginationControls';
import { deleteRecipe, getUserFavoritesPaginated, removeFromFavorites } from '../services/recipeService';
import { useLanguage } from '../i18n/LanguageContext';
import './ExplorerPage.css';
import './FavoritesPage.css';

const PAGE_SIZE = 10;

const FavoritesPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useOutletContext() ?? {};
  const { lang, t } = useLanguage();
  const f = t.favorites;

  const [recipes, setRecipes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeActionId, setActiveActionId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [searchScope, setSearchScope] = useState('title');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Share handler
  const handleShare = (recipe) => {
    const shareText = getRecipeShareText(recipe, lang);
    const url = window.location.origin + window.location.pathname + (lang === 'he' ? `#/heb/recipes/${recipe._id || recipe.id}` : `#/recipes/${recipe._id || recipe.id}`);
    if (navigator.share) {
      navigator.share({
        title: recipe.title,
        text: shareText,
        url,
      });
    } else {
      // Fallback: open a simple dialog with share links
      const whatsapp = `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + url)}`;
      const mailto = `mailto:?subject=${encodeURIComponent(recipe.title)}&body=${encodeURIComponent(shareText + '\n' + url)}`;
      window.open(whatsapp, '_blank');
      // Optionally, show a modal with more options
    }
  };

  const favoriteIds = useMemo(() => new Set(recipes.map((r) => r._id || r.id)), [recipes]);

  const headerContent = {
    eyebrow: f.eyebrow,
    title: f.title,
    accent: f.accent,
    description: f.description,
  };

  const handleRecipeSelect = (recipe) => {
    const recipeId = recipe?._id || recipe?.id;
    if (!recipeId) return;
    navigate(lang === 'he' ? `/heb/recipes/${recipeId}` : `/recipes/${recipeId}`, { state: { recipe } });
  };

  const handleRecipeEdit = (recipe) => {
    const canManage = currentUser?.role === 'ADMIN' || currentUser?.id === recipe?.recipeUserId;
    if (!canManage) return;
    navigate(lang === 'he' ? '/heb/submit' : '/submit', { state: { recipe, mode: 'edit' } });
  };

  const handleRecipeDelete = async (recipe) => {
    const recipeId = recipe?._id || recipe?.id;
    const canManage = currentUser?.role === 'ADMIN' || currentUser?.id === recipe?.recipeUserId;
    if (!canManage || !recipeId) return;

    const shouldDelete = typeof window === 'undefined' || window.confirm(`Delete "${recipe.title}"?`);
    if (!shouldDelete) return;

    setActiveActionId(recipeId);
    try {
      await deleteRecipe(recipeId);
      setRecipes((prev) => prev.filter((entry) => (entry?._id || entry?.id) !== recipeId));
      setError(null);
    } catch (err) {
      setError(err?.message ?? 'Unable to delete this recipe.');
    } finally {
      setActiveActionId(null);
    }
  };

  const handleFavoriteToggle = async (recipe) => {
    const recipeId = recipe._id || recipe.id;
    // On favorites page, toggling always means removing
    setRecipes((prev) => prev.filter((r) => (r._id || r.id) !== recipeId));
    try {
      await removeFromFavorites(recipeId);
    } catch {
      // Revert
      setRecipes((prev) => [...prev, recipe]);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();

    setIsLoading(true);
    getUserFavoritesPaginated(
      { page: currentPage, pageSize: PAGE_SIZE, search: searchQuery, searchScope },
      controller.signal
    )
      .then(({ favorites, totalPages: pages }) => {
        if (!isMounted) return;
        setRecipes(favorites);
        setTotalPages(pages);
        setError(null);
      })
      .catch((err) => {
        if (!isMounted || controller.signal.aborted) return;
        setError(err?.message ?? 'Unable to load favorites.');
      })
      .finally(() => {
        if (isMounted && !controller.signal.aborted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [currentUser, currentPage, searchQuery, searchScope]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, searchScope]);

  return (
    <div className="favorites-page">
      <main className="favorites-main">
        <div className="explorer-header-row">
          <ExplorerHeader {...headerContent} />
        </div>
        <div className="explorer-search-bar" dir={lang === 'he' ? 'rtl' : undefined}>
          <span className="material-symbols-rounded explorer-search-icon" aria-hidden="true">search</span>
          <input
            className="explorer-search-input"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.explorer.searchPlaceholder}
            aria-label={t.explorer.searchPlaceholder}
          />
          <div className="explorer-search-scope">
            <button
              type="button"
              className={`explorer-scope-btn${searchScope === 'title' ? ' is-active' : ''}`}
              onClick={() => setSearchScope('title')}
            >
              {t.explorer.searchScopeTitle}
            </button>
            <button
              type="button"
              className={`explorer-scope-btn${searchScope === 'content' ? ' is-active' : ''}`}
              onClick={() => setSearchScope('content')}
            >
              {t.explorer.searchScopeContent}
            </button>
          </div>
        </div>
        {!currentUser ? (
          <div className="recipe-empty-state">
            <p>{f.loginPrompt}</p>
          </div>
        ) : (
          <>
            <RecipeGrid
              recipes={recipes}
              isLoading={isLoading}
              error={error}
              onRecipeSelect={handleRecipeSelect}
              onRecipeEdit={handleRecipeEdit}
              onRecipeDelete={handleRecipeDelete}
              activeActionId={activeActionId}
              currentUser={currentUser}
              favoriteIds={favoriteIds}
              onFavoriteToggle={handleFavoriteToggle}
              emptyMessage={searchQuery.trim() ? t.explorer.searchNoResults : f.empty}
              onShare={handleShare}
            />
            {totalPages > 1 && (
              <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} lang={lang} />
            )}
          </>
        )}
      </main>
      <PageFooter />
    </div>
  );
};

export default FavoritesPage;
